/**
 * Attribution — one service you deliver, every funder that would pay for it.
 *
 * Funders you already bill come first, then funders the finder matched that
 * you have not added yet. Each shows the channel a claim would go out on, as
 * the submission router resolves it, so the org can see how one encounter is
 * attributed across existing and new funders.
 */
import React, { useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { SectionHeader, PageState } from '@/components/weavein';
import type { AttributionFunder, AttributionResult, CapabilityItem, FundingApi } from '@/services/api/fundingOpportunities';
import { CARD, MUTED, ErrorNote, Pill, STATUS_LABEL, channelLabel, useLoad } from './shared';

const FunderRow: React.FC<{ f: AttributionFunder }> = ({ f }) => (
  <li className="flex flex-wrap items-center justify-between gap-2 border-b border-v2border-muted py-2.5 last:border-0">
    <div className="min-w-0">
      <p className="text-[14px] font-medium text-ink">{f.funder_name}</p>
      <p className="text-[12px] text-ink-400">
        {f.program ? `${f.program} · ` : ''}
        {channelLabel(f.channel)}
      </p>
    </div>
    <div className="flex items-center gap-2">
      {!f.is_existing && f.status && <Pill tone="accent">{STATUS_LABEL[f.status] ?? f.status}</Pill>}
      <Pill tone={f.is_existing ? 'success' : 'attention'}>{f.is_existing ? 'Already billing' : 'New funder'}</Pill>
    </div>
  </li>
);

const AttributionPanel: React.FC<{ api: FundingApi }> = ({ api }) => {
  const { data: items, error, loading } = useLoad(() => api.listCapabilities(), [api]);
  const confirmed = (items ?? []).filter((i) => i.status === 'confirmed');
  const [selected, setSelected] = useState<number | null>(null);
  const [result, setResult] = useState<AttributionResult | null>(null);
  const [loadingResult, setLoadingResult] = useState(false);
  const [resultError, setResultError] = useState<string | null>(null);

  useEffect(() => {
    if (selected == null && confirmed.length > 0) setSelected(confirmed[0].id);
  }, [confirmed, selected]);

  useEffect(() => {
    if (selected == null) return;
    let cancelled = false;
    setLoadingResult(true);
    setResultError(null);
    api
      .attribution({ item_id: selected })
      .then((r) => !cancelled && setResult(r))
      .catch((e) => !cancelled && setResultError(e instanceof Error ? e.message : String(e)))
      .finally(() => !cancelled && setLoadingResult(false));
    return () => {
      cancelled = true;
    };
  }, [api, selected]);

  const item: CapabilityItem | undefined = confirmed.find((i) => i.id === selected);
  const existing = (result?.funders ?? []).filter((f) => f.is_existing);
  const fresh = (result?.funders ?? []).filter((f) => !f.is_existing);

  if (loading && !items) return <PageState variant="loading" />;
  if (error) return <ErrorNote message={error} />;
  if (confirmed.length === 0) {
    return (
      <PageState
        variant="empty"
        title="Confirm a capability first"
        message="Attribution shows, for a service you deliver, every funder that would pay for it. Confirm your services on the Capabilities tab."
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className={`${CARD} space-y-3 p-4`}>
        <SectionHeader
          title="One service, every funder"
          description="Pick a service you deliver. Funders you already bill come first, then new funders you qualify for."
        />
        <select
          value={selected ?? ''}
          onChange={(e) => setSelected(Number(e.target.value))}
          className="h-9 w-full max-w-md rounded-md border border-v2border bg-surface px-2 text-[13px] text-ink"
          aria-label="Service"
        >
          {confirmed.map((i) => (
            <option key={i.id} value={i.id}>
              {i.label}
              {i.service_codes.length ? ` (${i.service_codes.join(', ')})` : ''}
            </option>
          ))}
        </select>
        {item && (
          <p className={`${MUTED} flex items-center gap-1`}>
            One encounter for {item.label} <ArrowRight className="h-3.5 w-3.5" /> {result?.funders.length ?? 0} funders
          </p>
        )}
      </div>

      {resultError && <ErrorNote message={resultError} />}
      {loadingResult && !result && <PageState variant="loading" />}
      {result && result.funders.length === 0 && (
        <PageState variant="empty" title="No funders for this service yet" message="Run a scan on the Opportunities tab to find funders that pay for it." />
      )}

      {existing.length > 0 && (
        <div className={`${CARD} p-4`}>
          <SectionHeader title={`Funders you already bill (${existing.length})`} />
          <ul className="mt-1">{existing.map((f, i) => <FunderRow key={`e${i}`} f={f} />)}</ul>
        </div>
      )}
      {fresh.length > 0 && (
        <div className={`${CARD} p-4`}>
          <SectionHeader title={`New funders that would also pay (${fresh.length})`} />
          <ul className="mt-1">{fresh.map((f, i) => <FunderRow key={`n${i}`} f={f} />)}</ul>
        </div>
      )}
    </div>
  );
};

export default AttributionPanel;
